import { useState, useEffect, useMemo } from 'react';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { LocalizedLink } from '@/components/LocalizedLink';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';

export const Header = () => {
  const { t } = useTranslation('common');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = useMemo(
    () => [
      { label: t('nav.philosophy'), href: '/#filosofia' },
      { label: t('nav.meats'), href: '/#carni' },
      { label: t('nav.fire'), href: '/#fuoco' },
      { label: t('nav.location'), href: '/location' },
      { label: t('nav.giftCard'), href: '/gift-card' },
      { label: t('nav.careers'), href: '/lavora-con-noi' },
      { label: t('nav.contacts'), href: '/#prenotazione' },
    ],
    [t],
  );

  return (
    <>
      <header
        role="banner"
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background/95 backdrop-blur-sm py-3 md:py-4'
            : 'bg-transparent py-4 md:py-6'
        } ${isMobileMenuOpen ? 'border-b-0' : ''}`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6">
          <LocalizedLink href="/" className="flex items-center shrink-0" aria-label={t('aria.home')}>
            <img
              src="/logo.png"
              alt={t('logoAlt')}
              className={`h-12 w-auto md:h-12 object-contain transition-all duration-300 ease-out ${
                isMobileMenuOpen ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
              }`}
              width="120"
              height="48"
              loading="eager"
            />
          </LocalizedLink>

          <nav className="hidden items-center gap-8 lg:flex" role="navigation" aria-label={t('aria.desktopNav')}>
            {navLinks.map((link) => (
              <LocalizedLink
                key={link.href}
                href={link.href}
                className="text-sm font-light uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground underline-hover"
              >
                {link.label}
              </LocalizedLink>
            ))}
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <LanguageSwitcher />
            <Button variant="outline" size="sm" asChild>
              <LocalizedLink href="/prenota" aria-label={t('aria.bookTable')}>
                {t('cta.book')}
              </LocalizedLink>
            </Button>
          </div>

          <div className="flex items-center gap-3 lg:hidden">
            <LanguageSwitcher />
            <button
              type="button"
              className="focus-ring text-foreground z-[60] relative shrink-0 rounded-sm"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label={t('aria.openMenu')}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent
          id="mobile-menu"
          side="right"
          className="flex h-full w-full max-w-full flex-col border-0 p-0 sm:max-w-full lg:hidden [&>button]:hidden"
          aria-describedby={undefined}
        >
          <SheetTitle className="sr-only">{t('aria.mobileNav')}</SheetTitle>

          <div className="flex shrink-0 items-center justify-end px-4 py-4 md:px-6 md:py-6">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="focus-ring rounded-sm text-foreground transition-colors hover:text-primary"
              aria-label={t('aria.closeMenu')}
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="flex flex-1 flex-col overflow-y-auto overscroll-contain">
            <nav
              className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-8 md:gap-8 md:py-12 md:px-6"
              role="navigation"
              aria-label={t('aria.mobileNavLinks')}
            >
              {navLinks.map((link) => (
                <LocalizedLink
                  key={link.href}
                  href={link.href}
                  className="text-center text-base font-light uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground md:text-lg"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </LocalizedLink>
              ))}
            </nav>
            <div className="flex justify-center pb-10">
              <LanguageSwitcher variant="mobile" />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
